import * as dateFns from 'date-fns'
import * as fpDateFns from 'date-fns/fp'
import { enGB as en, es, fr } from 'date-fns/locale'
import i18n from './i18n'

export const $date = {
  ...dateFns,
  // https://github.com/date-fns/date-fns/issues/1532#issuecomment-599940816
  fp: fpDateFns,
  /** Returns String noting how long ago a timestamp was.
   * Uses i18n.locale to populate the correct locale phrasing.
   *
   * A timestamp that resolves slightly ahead of now — clock skew between the
   * API host and the browser — is clamped to now, so it reads "0 seconds ago"
   * rather than a misleading past-tense distance in the wrong direction.
   *
   * @example [strict=true] '11 hours ago'
   * @example [strict=false]'about 1 year ago'
  */
  $formatTimeAgo (value, { strict = true } = {}) {
    const locale = { en, es, fr }[i18n.locale]
    const date = $date.$getTimestamp(value)
    if (!date) return null
    const now = $date.$getTimestamp(new Date())
    const clamped = Math.min(date, now)
    return strict
      ? i18n.global.t('time.ago', [dateFns.formatDistanceToNowStrict(clamped, { locale })])
      : i18n.global.t('time.ago', [dateFns.formatDistanceToNow(clamped, { locale })])
  },

  /** Returns compact relative time without suffix (ago/in).
   * Calculates absolute distance from now and formats in minutes, hours or days.
   *
   * @example '45m'
   * @example '3h'
   * @example '2d'
   */

  $formatRelative (value) {
    const date = $date.$getTimestamp(value)
    if (!date) return null

    const now = $date.$getTimestamp(new Date())
    const diffMs = Math.abs(date - now)

    const minutes = Math.floor(diffMs / (60 * 1000))
    const hours = Math.floor(diffMs / (60 * 60 * 1000))
    const days = Math.floor(diffMs / (24 * 60 * 60 * 1000))

    if (minutes < 60) return `${minutes}m`
    if (hours < 24) return `${hours}h`
    return `${days}d`
  },

  /** Return a diff object with years, months, days, hours, minutes and seconds.
   *
   * @example [start=1714643400] [end=1714643408] { years: 0, months: 0, days: 0, hours: 0, minutes: 0, seconds: 8 }
   */
  $diff (start, end) {
    return {
      years: dateFns.differenceInYears(end, start),
      months: dateFns.differenceInMonths(end, start),
      days: dateFns.differenceInDays(end, start),
      hours: dateFns.differenceInHours(end, start) % 24,
      minutes: dateFns.differenceInMinutes(end, start) % 60,
      seconds: dateFns.differenceInSeconds(end, start) % 60,
    }
  },

  /** Returns an array of 12 months in the current locale */
  $months () {
    return Array.from({ length: 12 }, (_, i) => $date.format(new Date(2021, i, 1), 'LLLL', { locale: this.$dateLocale }))
  },

  /** Returns an array of 7 weekdays in the current locale */
  $weekdays () {
    return Array.from({ length: 7 }, (_, i) => $date.format(new Date(2021, 0, i + 1), 'EEEE', { locale: this.$dateLocale }))
  },

  /** Returns a timestamp in ms (13 chars) or s (10 chars).
   *
   * @example [returnSeconds=true]  1650651285      # returns s
   * @example [returnSeconds=false] 1650651285735   # returns ms
   */
  $getTimestamp (value, { returnSeconds = false } = {}) {
    if (_.$isEmpty(value)) return null
    let date
    if (_.$isPrimitive(value) && /^-?\d+$/.test(`${value}`)) {
      // Unix timestamp in seconds (10 digits) or milliseconds (13).
      date = new Date(Number(`${value}`.padEnd(13, '0')))
    } else if (typeof value === 'string') {
      // Datetime string: treat a timezone-less (naive) value as UTC so the
      // instant is not shifted by the viewer's local offset. A bare date and a
      // value that already carries a zone are left as-is.
      const isoish = value.trim().replace(' ', 'T')
      const hasZone = /(Z|[+-]\d{2}:?\d{2})$/.test(isoish)
      date = new Date(isoish.includes('T') && !hasZone ? `${isoish}Z` : isoish)
    } else {
      date = value
    }
    if (!dateFns.isValid(date)) return null
    return Number(dateFns.format(date, returnSeconds ? 't' : 'T'))
  },

  /** Checks if the given value is a valid Unix timestamp (in seconds or milliseconds).
   *
   * Accepts either a 10-digit (seconds) or 13-digit (milliseconds) timestamp.
   *
   *
   * @example $isValidTimestamp(1650651285)     // true (seconds)
   * @example $isValidTimestamp(1650651285735)  // true (milliseconds)
   * @example $isValidTimestamp('1650651285')   // null (not a number)
   * @example $isValidTimestamp(NaN)            // null
   * @example $isValidTimestamp(18446744011573955000) // null (corrupted or misinterpreted timestamp)
   *
   */
  $getValidDateFromTimestamp (ts) {
    if (typeof ts !== 'number' || isNaN(ts)) return null

    let date = dateFns.fromUnixTime(ts)
    if (dateFns.isValid(date)) return date

    date = new Date(ts)
    return dateFns.isValid(date) ? date : null
  },
}

window.$date = $date

export default {
  /** @param {import('vue').App} app - The Vue instance. */
  install (app) {
    app.config.globalProperties.$date = $date
    app.mixin({
      // So these will be available to each template too.
      computed: {
        $date: () => $date,
        $dateLocale: () => ({ en, es, fr }[i18n.global.locale.value]), // ? needed for some date-fns that use { locale } option
      },
    })
  },
}
